import * as React from "react";
import styled, { css } from "styled-components";
import { select } from "@hubper/theme";
import Text from "../../elements/Text";
import Icon from "../../elements/Icon";
import mergeStyles from "../../util/mergeStyles";

const formatSeconds = (seconds: number): string => {
  return [Math.floor(seconds / 60), Math.floor(seconds % 60)]
    .map(x => x.toString())
    .map(x => (x.length === 1 ? `0${x}` : x))
    .join(":");
};

const Base = styled.div<{ selected: boolean }>`
  user-select: none;
  box-sizing: border-box;
  transition: background-color 0.2s;
  border-radius: ${select.radius("light")};
  padding: 28px 32px;
  padding-right: 64px;
  border: 1px solid ${select.color(["grey", 2])};
  position: relative;
  display: flex;
  background-color: ${p =>
    p.selected ? select.color(["grey", 0], p) : "white"};

  ${p =>
    !p.selected &&
    css`
      &:hover {
        background-color: ${select.color(["grey", 0])};
      }
    `}
`;

const Left = styled.div`
  width: 100px;
  display: flex;
  align-items: center;
`;

const Right = styled.div`
  flex: 1 1;
`;

const Button = styled.div`
  transition: 0.2s;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${select.color(["grey", 2])};
  background-color: white;
  cursor: pointer;

  & > * {
    fill: ${select.color(["grey", 3])};
  }

  &:hover {
    transform: scale(1.04);

    box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.15);

    & > * {
      fill: ${select.color(["grey", 5])};
    }
  }

  &:active {
    transform: scale(1.02);
    box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0);
  }
`;

type PlaybackStatus = "play" | "pause";

type Props = {
  style?: React.CSSProperties;
  className?: string;
  title: string;
  description: string;
  dateString: string;
  duration: number;
  selected: boolean;
  onSelect: () => void;
  onPlaybackStatusChange: (status: PlaybackStatus) => void;
  playbackStatus: PlaybackStatus;
};

function PodcastBoxItem(
  {
    style,
    className,
    selected,
    title,
    description,
    dateString,
    duration,
    onSelect,
    playbackStatus,
    onPlaybackStatusChange
  }: Props,
  ref: any
) {
  return (
    <Base
      selected={selected}
      ref={ref}
      style={mergeStyles(style, {
        cursor: selected ? "default" : "pointer"
      })}
      className={className}
      onClick={onSelect}
    >
      <Left>
        <Button
          onClick={evt => {
            evt.preventDefault();
            evt.stopPropagation();
            onPlaybackStatusChange(
              playbackStatus === "pause" ? "play" : "pause"
            );
          }}
        >
          <Icon
            c="inherit"
            size={24}
            name={playbackStatus === "pause" ? "video" : "pause"}
          />
        </Button>
      </Left>
      <Right>
        <Text fw="heavy">{title}</Text>
        <Text style={{ marginTop: 6, lineHeight: 1.3 }} fs={-2} c={["grey", 5]}>
          {description}
        </Text>
      </Right>

      <Text
        c={["grey", 4]}
        fs={-3}
        style={{ position: "absolute", top: 8, right: 16 }}
      >
        {dateString}
      </Text>
      <Text
        c={["grey", 4]}
        fs={-3}
        style={{ position: "absolute", bottom: 8, right: 16 }}
      >
        <Icon style={{ marginRight: 4 }} size={12} name="time" />
        {formatSeconds(duration)}
      </Text>
    </Base>
  );
}

export default React.forwardRef(PodcastBoxItem);
